import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton
} from '@mui/material';

export default function SevenDayNotice({ open, setOpen, tenancyDetails, customersDetails }) {

  // Convert check-in to weekday
const getWeekday = (dateString) => {
  if (!dateString) return '';

  const [year, month, day] = dateString.split('-').map(Number);

  // Create date using local timezone, no UTC conversion
  const d = new Date(year, month - 1, day);

  return d.toLocaleDateString('en-US', { weekday: 'long' });
};

  // Today's date
  const today = new Date().toISOString().split('T')[0];

  // Convert tenancyDetails.checkout into day / month / year
  const parseDate = (d) => {
  if (!d) return { day: '', month: '', year: '' };

  // Expecting format: YYYY-MM-DD
  const [year, month, day] = d.split('-');

  const monthName = new Date(year, month - 1, day).toLocaleString('en-US', {
    month: 'long'
  });

  return {
    day: Number(day),
    month: monthName,
    year: Number(year),
  };
};

  const vacate = parseDate(tenancyDetails?.checkout);

  const [formData, setFormData] = useState({
    date: today,
    tenantName:  '',
    rentalPeriodStart: getWeekday(tenancyDetails.checkin),

    vacateDay: vacate.day,
    vacateMonth: vacate.month,
    vacateYear: vacate.year,

    landlordName: 'Tidy Inn LLC',
    landlordAddress: '1601 George Jenkins Blvd, Lakeland, FL 33815',
    landlordPhone: '(863) 825-5007',

    serviceDate: new Date().getDate(),
    serviceMonth: new Date().toLocaleString('en-US', { month: 'long' }),
    serviceYear: new Date().getFullYear(),

    serviceMethod: 'personal'
  });

  useEffect(() => {
    // Sync props if they change
    setFormData((prev) => ({
      ...prev,
      tenantName:  prev.tenantName,
      rentalPeriodStart: getWeekday(tenancyDetails?.checkin),
      vacateDay: vacate.day,
      vacateMonth: vacate.month,
      vacateYear: vacate.year
    }));
  }, [ tenancyDetails?.checkin, tenancyDetails?.checkout]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
            Notice to Vacate Form
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Typography sx={{ fontSize: 20 }}>×</Typography>
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 4 }}>
          <Box id="printable-content" sx={{ maxWidth: 700, mx: 'auto' }}>

            {/* Date + Tenant Name */}
            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={handleChange('date')}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="To (Tenant Name)"
                value={formData.tenantName}
                onChange={handleChange('tenantName')}
                placeholder="Enter tenant name"
              />
            </Box>

            {/* Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                NOTICE TO VACATE
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                (7-DAYS)
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Week-to-week paragraph */}
            <Box sx={{ mb: 4 }}>
              <Typography paragraph>
                You are a week-to-week tenant in the above-described premises, your rental period begins on{' '}
                <TextField
                  value={formData.rentalPeriodStart}
                  onChange={handleChange('rentalPeriodStart')}
                  placeholder="day of week"
                  size="small"
                  variant="standard"
                  sx={{ width: 120, mx: 1 }}
                />
                of each week.
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Main notice paragraph */}
            <Box sx={{ mb: 4 }}>
              <Typography paragraph sx={{ lineHeight: 1.8 }}>
                You are hereby notified that your tenancy is being terminated. You are required to vacate the
                premises before the first day of the next rental period, to wit: before the{' '}
                <TextField
                  value={formData.vacateDay}
                  onChange={handleChange('vacateDay')}
                  placeholder="day"
                  size="small"
                  variant="standard"
                  sx={{ width: 50, mx: 0.5 }}
                />
                day of{' '}
                <TextField
                  value={formData.vacateMonth}
                  onChange={handleChange('vacateMonth')}
                  placeholder="month"
                  size="small"
                  variant="standard"
                  sx={{ width: 100, mx: 0.5 }}
                />
                ,{' '}
                <TextField
                  value={formData.vacateYear}
                  onChange={handleChange('vacateYear')}
                  placeholder="year"
                  size="small"
                  variant="standard"
                  sx={{ width: 70, mx: 0.5 }}
                />
                . This notice is being served upon you not less than 7 days prior to the end of the applicable rental period.
              </Typography>
            </Box>

            {/* Landlord info */}
            <Box sx={{ mb: 4 }}>
              <TextField fullWidth label="Landlord or Property Manager" value={formData.landlordName} onChange={handleChange('landlordName')} sx={{ mb: 2 }} />
              <TextField fullWidth label="Address" value={formData.landlordAddress} onChange={handleChange('landlordAddress')} sx={{ mb: 2 }} />
              <TextField fullWidth label="Phone" value={formData.landlordPhone} onChange={handleChange('landlordPhone')} />
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Certification */}
            <Box sx={{ mb: 3 }}>
              <Typography paragraph sx={{ lineHeight: 1.8 }}>
                I certify that a true copy of the foregoing Notice to Vacate was served on the above-named tenant
                on the{' '}
                <TextField value={formData.serviceDate} onChange={handleChange('serviceDate')} placeholder="day" size="small" variant="standard" sx={{ width: 50, mx: 0.5 }} />
                day of{' '}
                <TextField value={formData.serviceMonth} onChange={handleChange('serviceMonth')} placeholder="month" size="small" variant="standard" sx={{ width: 100, mx: 0.5 }} />
                ,{' '}
                <TextField value={formData.serviceYear} onChange={handleChange('serviceYear')} placeholder="year" size="small" variant="standard" sx={{ width: 70, mx: 0.5 }} />
                in the following manner:
              </Typography>

              <FormControl component="fieldset" sx={{ pl: 2 }}>
                <RadioGroup value={formData.serviceMethod} onChange={handleChange('serviceMethod')}>
                  <FormControlLabel value="personal" control={<Radio />} label="By personally serving same upon said tenant." />
                  <FormControlLabel value="posting" control={<Radio />} label="By posting same at the above-described premises in the absence of said tenant." />
                </RadioGroup>
              </FormControl>
            </Box>

          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" size="large">Cancel</Button>
          <Button onClick={handlePrint} variant="contained" size="large">Print</Button>
        </DialogActions>
      </Dialog>

      {/* PRINT STYLE */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-content, #printable-content * { visibility: visible; }
          #printable-content { position: absolute; left: 0; top: 0; width: 100%; padding: 60px; }
          #printable-content input {
            border: none !important;
            border-bottom: 1px solid #000 !important;
            outline: none !important;
            background: transparent !important;
          }
          @page { margin: 0.5in; }
        }
      `}</style>
    </>
  );
}
